import { useEffect } from "react";
import { TASKBAR_H } from "./windowManager/snap";

// Raise the RC container so its bottom sits just above the taskbar. We use
// margin-bottom (independent of RC's animated `transform`, so expand/collapse
// stays smooth). RC already lifts itself ~15px via its own transform.
//   final bottom = viewportBottom - margin - 15 ; taskbar top = viewportBottom - 48
//   flush → margin = 48 - 15 = 33 ; add a small gap above the taskbar.
const RC_GAP = 4; // small gap between RC and the taskbar
const RC_EDGE_OFFSET = 15; // RC's own transform offset from its anchor
const RC_MARGIN = `${TASKBAR_H - RC_EDGE_OFFSET + RC_GAP}px`; // 48 - 15 + 4 = 37px

/**
 * useRcAboveTaskbar — keep the RingCentral widget pinned to the top of the
 * taskbar (expanded AND minimized) so it never overlaps the tray.
 *
 * The pinned element is the fixed RC container (DIV#rc-widget .Adapter_root,
 * `position:fixed; bottom:0`, positioned/animated via `transform`). RC OWNS and
 * continually re-applies that element's `bottom`/`margin`/`transform`, so any
 * one-time style override is wiped. We therefore re-assert `margin-bottom`
 * whenever RC restyles the element, using a MutationObserver SCOPED to that one
 * element's `style` attribute only.
 *
 * Rule 7 note: the perf-problem observer that rule warns about is a
 * `subtree:true` observer on <body> firing on every app-wide DOM change. This is
 * the opposite — a single element, `attributeFilter:['style']` — firing only
 * when RC restyles its own widget. We never touch RC's z-index or transform, so
 * its animation stays smooth.
 */
const findRcContainer = () => {
  const candidates = document.querySelectorAll('#rc-widget, [class*="Adapter_root"]');
  for (let i = 0; i < candidates.length; i += 1) {
    if (window.getComputedStyle(candidates[i]).position === "fixed") return candidates[i];
  }
  return null;
};

export const useRcAboveTaskbar = () => {
  useEffect(() => {
    let target = null;
    let observer = null;
    let selfMutating = false;

    const apply = () => {
      if (!target || target.style.marginBottom === RC_MARGIN) return;
      selfMutating = true; // ignore the mutation our own change triggers
      target.style.setProperty("margin-bottom", RC_MARGIN, "important");
      selfMutating = false;
    };

    const attach = () => {
      if (target) return true;
      const el = findRcContainer();
      if (!el) return false;
      target = el;
      apply();
      observer = new MutationObserver(() => {
        if (!selfMutating) apply();
      });
      observer.observe(target, { attributes: true, attributeFilter: ["style"] });
      return true;
    };

    // RC loads async — retry until the container exists, then the observer holds it.
    const timers = attach() ? [] : [300, 800, 1500, 3000, 6000].map((ms) => setTimeout(attach, ms));

    return () => {
      timers.forEach(clearTimeout);
      if (observer) observer.disconnect();
      if (target) target.style.removeProperty("margin-bottom");
    };
  }, []);
};

export default useRcAboveTaskbar;
